/**
 * "Where are things?" on the project Home (design "A calmer project Home"): Apps and Files. A part with nothing to show
 * disappears, and the card with it.
 *
 * Apps, in a sandbox (sandbox-first), is the same table as an expanded project in the sandbox's "Running now"
 * (sandbox/RunningNow.jsx, its TotalsRow and ServiceRow): the project's totals (CPU and memory summed, the longest
 * uptime), then one row per service: its name linking to its Code view, its state with restarts, CPU, memory, uptime,
 * then Start / Stop, Open and Logs, and a "⋯" with Restart, Files, Configuration and Open app in a new tab, all on one line. Without a
 * sandbox (no runner, no state) one plain row per service: its name, its stack, Open and the "⋯".
 *
 * Apps' "+" (owners and editors; Apps shows once there is a service, a brand-new project has "Start by…"): a small chooser (AddService.jsx, the same ways as the service dropdown's "+ Create new service"): an empty service
 * (`onAddService('new')`), a .zip or a folder (picked here and handed over as `onAddService('import', collected)`,
 * collectEntries' shape, as the Home's drop), a repository (`onAddService('git')`); the Home does the adding.
 *
 * No apps yet (a project with only its brain, utils/projectLayout.js, once it has files of its own so the Home is not
 * "Start by…"): "No apps yet" with the ways to make one, Add an app (the same chooser) and Ask the assistant
 * (`onAskAssistant`, where chat is on); no Start all, no rows. A viewer, who can do neither, gets no card.
 *
 * Files: one line "Inputs (n) · Results (n)" with "add files" (the whole page takes a drop).
 *
 * `services`: `[[key, service]]` (the project configuration's); `runner(key)`: the sandbox entry of a service, or null
 * (never started, or not sandbox-first).
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'antd';
import DeleteServiceDialog, { showDeletedMessage } from '../apps/DeleteServiceDialog';
import { isRunning } from '../../services/SandboxService';
import useRepoFlags from '../../hooks/useRepoFlags';
import { Plus } from '@phosphor-icons/react';
import { Head, ServiceRow, TotalsRow, serviceSortValue, sortRunItems, useRunSort } from '../sandbox/RunningNow';
import { appUrlOf, openApp } from '../sandbox/projectRun';
import { servicePath, serviceStack } from '../../utils/projectOverview';
import { openHistory } from '../../utils/workspaceModel';
import { usePlanTab } from '../../hooks/usePlanFeatures';
import { AddServiceMenu } from './AddService';

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;
const shortName = (serviceKey, service) => String(service.displayName || service.name || serviceKey).replace(/^services\//, '');

/**
 * A service's "⋯": Restart while it runs (`onRestart`, who can run it), Files, Configuration (variables, secrets, jobs)
 * and, while it runs, Open app in a new tab. In a narrow table Open and Logs move in here too (`onOpen`, `onLogs`).
 */
function MoreMenu({
  projectId, serviceKey, service, entry = null, sandbox = false, onOpenFiles, canDelete = false, onDeleted = null, onRestart = null, onOpen = null, onLogs = null,
}) {
  const [confirming, setConfirming] = useState(false);
  const name = servicePath(serviceKey);
  const short = shortName(serviceKey, service);
  const appUrl = sandbox ? appUrlOf(projectId, { serviceName: name, service: entry }) : null;
  const shows = usePlanTab();
  // A context-only service (No build) is never restarted (as ServiceActions, which has no Restart for it).
  const contextOnly = Boolean(useRepoFlags(projectId).serviceFlags(name)?.noBuild);
  const restart = onRestart && isRunning(entry) && !contextOnly;
  const items = [
    ...(onOpen ? [{ key: 'open', label: 'Open in Flow' }] : []),
    ...(onLogs ? [{ key: 'logs', label: 'Logs' }] : []),
    ...(restart ? [{ key: 'restart', label: 'Restart' }] : []),
    ...(onOpen || onLogs || restart ? [{ type: 'divider' }] : []),
    { key: 'files', label: 'Files' },
    ...(name.startsWith('services/') && shows('config') ? [{ key: 'config', label: 'Configuration' }] : []),
    ...(appUrl ? [{ key: 'app', label: 'Open app in a new tab' }] : []),
    ...(canDelete && name.startsWith('services/') ? [{ type: 'divider' }, { key: 'delete', label: 'Delete service…', danger: true }] : []),
  ];
  const onClick = ({ key }) => {
    if (key === 'open') { onOpen(); }
    if (key === 'logs') { onLogs(); }
    if (key === 'restart') { onRestart(); }
    if (key === 'files') { onOpenFiles(name); }
    if (key === 'config') { openHistory({ tab: 'config', focus: { service: name.replace(/^services\//, '') } }); }
    if (key === 'app') { openApp(projectId, { serviceName: name, service: entry }); }
    if (key === 'delete') { setConfirming(true); }
  };
  // The same dialog and delete as Apps' (apps/DeleteServiceDialog: vfs-service stops it, removes its folder as one
  // commit and cleans its pins, default app, secrets and jobs); the Home lists what is left.
  const deleted = (done) => {
    setConfirming(false);
    showDeletedMessage(projectId, done, { onRestored: () => onDeleted?.() });
    onDeleted?.();
  };
  return (
    <>
      <Dropdown menu={{ items, onClick }} placement="bottomRight" trigger={['click']}>
        <button aria-label={`More for ${short}: ${restart ? 'restart, ' : ''}files, configuration${appUrl ? ', open app' : ''}${canDelete ? ', delete' : ''}`} className="ph-link ph-more" title="More" type="button">⋯</button>
      </Dropdown>
      {confirming && <DeleteServiceDialog onClose={() => setConfirming(false)} onDeleted={deleted} open projectId={projectId} service={name} />}
    </>
  );
}

MoreMenu.propTypes = {
  canDelete: PropTypes.bool,
  onLogs: PropTypes.func,
  onOpen: PropTypes.func,
  onRestart: PropTypes.func,
  onDeleted: PropTypes.func,
  entry: PropTypes.object,
  onOpenFiles: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  sandbox: PropTypes.bool,
  service: PropTypes.object.isRequired,
  serviceKey: PropTypes.string.isRequired,
};

/** Without a sandbox: a service's name and stack, Open and the "⋯". */
function AppRow({ projectId, serviceKey, service, technologies, onOpen, onOpenFiles }) {
  const label = service.displayName || service.name || serviceKey;
  const short = shortName(serviceKey, service);
  return (
    <li className="ph-app" data-testid="home-app">
      <span className="ph-app-name" title={label}>{short}</span>
      <span className="ph-muted ph-small ph-app-state">{serviceStack(serviceKey, service, technologies)}</span>
      <span className="ph-app-actions">
        <button className="ph-link" onClick={() => onOpen(serviceKey, service)} title={`Open ${short} in Flow`} type="button">Open</button>
        <MoreMenu onOpenFiles={onOpenFiles} projectId={projectId} service={service} serviceKey={serviceKey} />
      </span>
    </li>
  );
}

AppRow.propTypes = {
  onOpen: PropTypes.func.isRequired,
  onOpenFiles: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  service: PropTypes.object.isRequired,
  serviceKey: PropTypes.string.isRequired,
  technologies: PropTypes.arrayOf(PropTypes.string),
};

/** The width of `element` (0 until measured, or where there is no ResizeObserver). */
function useWidth(element) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!element || typeof ResizeObserver === 'undefined') { return undefined; }
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  return width;
}

// Narrower than this the CPU, memory and uptime columns go; narrower still, Open and Logs move into the "⋯".
const NARROW_PX = 640;
const TIGHT_PX = 440;

/** One service of the Apps table: a compact ServiceRow, its "⋯" with Restart (which restarts it as the button would). */
function AppServiceRow({
  projectId, tenantId, serviceKey, service, entry, canRun, canDelete, tight, onDeleted, onChanged, onOpenService, onOpenCode, onOpenApp, onOpenFiles, onOpenLogs,
}) {
  const [restartRequest, setRestartRequest] = useState(0);
  const name = servicePath(serviceKey);
  return (
    <ServiceRow
      alwaysShowState
      canRun={canRun}
      compact
      extra={(
        <MoreMenu
          canDelete={canDelete}
          entry={entry}
          onDeleted={onDeleted}
          onLogs={tight && onOpenLogs ? () => onOpenLogs(name) : null}
          onOpen={tight ? () => onOpenService(serviceKey, service) : null}
          onOpenFiles={onOpenFiles}
          onRestart={canRun ? () => setRestartRequest((count) => count + 1) : null}
          projectId={projectId}
          sandbox
          service={service}
          serviceKey={serviceKey}
        />
      )}
      hideOpenLogs={tight}
      label={shortName(serviceKey, service)}
      onChanged={onChanged}
      onOpen={() => onOpenService(serviceKey, service)}
      onOpenApp={onOpenApp ? () => onOpenApp(serviceKey) : null}
      onOpenCode={onOpenCode ? () => onOpenCode(serviceKey, service) : null}
      onOpenLogs={onOpenLogs}
      projectId={projectId}
      restartRequest={restartRequest}
      service={entry}
      serviceName={name}
      showRemove={false}
      tenantId={tenantId}
      testId="home-app"
    />
  );
}

AppServiceRow.propTypes = {
  canDelete: PropTypes.bool,
  canRun: PropTypes.bool,
  entry: PropTypes.object,
  onChanged: PropTypes.func,
  onDeleted: PropTypes.func,
  onOpenApp: PropTypes.func,
  onOpenCode: PropTypes.func,
  onOpenFiles: PropTypes.func.isRequired,
  onOpenLogs: PropTypes.func,
  onOpenService: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  service: PropTypes.object.isRequired,
  serviceKey: PropTypes.string.isRequired,
  tenantId: PropTypes.string,
  tight: PropTypes.bool,
};

/**
 * In a sandbox: the project's totals, then one compact ServiceRow per service (one line each: the name and its state,
 * CPU, memory and uptime right-aligned, the actions in one group). A narrow table drops the numbers, then moves Open
 * and Logs into the "⋯", instead of wrapping or scrolling sideways.
 */
function AppsTable({ projectId, tenantId, services, runner, canRun, canDelete = false, onDeleted = null, onChanged, onOpenService, onOpenCode, onOpenApp = null, onOpenFiles, onOpenLogs }) {
  const entries = services.map(([key]) => runner(key)).filter(Boolean);
  // Every column sorts the services (the totals row stays on top).
  const [sort, sortBy] = useRunSort();
  const sorted = sortRunItems(services, sort, ([key, service], column) => serviceSortValue(runner(key), column, new Map(), shortName(key, service)));
  const [wrap, setWrap] = useState(null);
  const width = useWidth(wrap);
  const narrow = width > 0 && width < NARROW_PX;
  const tight = width > 0 && width < TIGHT_PX;
  const classes = ['sbx-table', 'sbx-run-table', 'is-compact', services.length > 1 && 'has-total', narrow && 'is-narrow'].filter(Boolean).join(' ');
  return (
    <div className="sbx-table-wrap ph-apps-table" ref={setWrap}>
      <table aria-label="Apps" className={classes}>
        <Head first="Service" onSort={sortBy} sort={sort} />
        <tbody>
          {/* One service: the row would only repeat it (a monorepo is one service). */}
          {services.length > 1 && (
            <TotalsRow
              className="sbx-run-project ph-apps-total"
              compact
              // Short and on one line (the side panel open leaves the name column narrow): "All · 2 services".
              name={(
                <span className="sbx-run-service ph-apps-total-name" title={`All services · ${plural(services.length, 'service')}`}>
                  All<span className="sbx-run-note"> · {plural(services.length, 'service')}</span>
                </span>
              )}
              services={entries}
              testId="home-apps-totals"
            />
          )}
          {sorted.map(([key, service]) => (
            <AppServiceRow
              canDelete={canDelete}
              canRun={canRun}
              entry={runner(key)}
              key={key}
              onChanged={onChanged}
              onDeleted={onDeleted}
              onOpenApp={onOpenApp}
              onOpenCode={onOpenCode}
              onOpenFiles={onOpenFiles}
              onOpenLogs={onOpenLogs}
              onOpenService={onOpenService}
              projectId={projectId}
              service={service}
              serviceKey={key}
              tenantId={tenantId}
              tight={tight}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

AppsTable.propTypes = {
  canDelete: PropTypes.bool,
  onDeleted: PropTypes.func,
  canRun: PropTypes.bool,
  onChanged: PropTypes.func,
  onOpenApp: PropTypes.func,
  onOpenCode: PropTypes.func,
  onOpenFiles: PropTypes.func.isRequired,
  onOpenLogs: PropTypes.func,
  onOpenService: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  runner: PropTypes.func.isRequired,
  services: PropTypes.array.isRequired,
  tenantId: PropTypes.string,
};

/** Apps' "+": what to add a service from (the same ways as the service dropdown's "+ Create new service"). */
function AddService({ onAdd }) {
  return (
    <AddServiceMenu onAdd={onAdd}>
      <button aria-label="Add a service" className="ph-link ph-apps-add" data-testid="home-add-service" title="Add a service: an empty one, or from a .zip, a folder or a git repository" type="button">
        <Plus aria-hidden size={14} weight="bold" />
      </button>
    </AddServiceMenu>
  );
}

AddService.propTypes = { onAdd: PropTypes.func.isRequired };

/** Apps with no app yet: what to do instead of an empty table (Add an app, Ask the assistant). */
function NoAppsYet({ onAdd = null, onAskAssistant = null }) {
  return (
    <div className="ph-apps-empty" data-testid="apps-empty">
      <p>No apps yet.</p>
      <p className="ph-muted ph-small">{'The brain folder holds the project\'s docs, memory and scripts: it is never run as an app.'}</p>
      <div className="ph-apps-empty-actions">
        {onAdd && (
          <AddServiceMenu onAdd={onAdd}>
            <button className="ph-btn" data-testid="apps-empty-add" title="An empty app, or one from a .zip, a folder or a git repository" type="button">Add an app</button>
          </AddServiceMenu>
        )}
        {onAskAssistant && <button className="ph-btn" data-testid="apps-empty-ask" onClick={onAskAssistant} type="button">Ask the assistant to build one</button>}
      </div>
    </div>
  );
}

NoAppsYet.propTypes = { onAdd: PropTypes.func, onAskAssistant: PropTypes.func };

export default function WhereThings({
  projectId, tenantId = null, services, runner, technologies = [], sandbox = false, canRun = false, onChanged, onOpenService, onOpenCode = null, onOpenApp = null,
  onOpenFiles, onOpenLogs = null, canEdit = false,
  onAddService = null, addState = null, runAll = null, onServiceDeleted = null, onAskAssistant = null,
}) {
  const canAdd = canEdit && Boolean(onAddService);
  // The project's files have their own card (FilesSection).
  if (services.length === 0) {
    // A viewer, or nowhere to add one from: nothing to show, so no card (the calmer Home).
    const ask = canEdit ? onAskAssistant : null;
    if (!canAdd && !ask) { return null; }
    return (
      <section aria-label="Where are things" className="ph-card ph-where" data-testid="where-things">
        <div className="ph-apps">
          <div className="ph-apps-head"><h2>Apps</h2></div>
          {addState && (
            <div className={`ph-apps-note ${addState.phase === 'failed' ? 'ph-error' : 'ph-muted ph-small'}`} role={addState.phase === 'failed' ? 'alert' : 'status'}>{addState.text}</div>
          )}
          <NoAppsYet onAdd={canAdd ? onAddService : null} onAskAssistant={ask} />
        </div>
      </section>
    );
  }
  return (
    <section aria-label="Where are things" className="ph-card ph-where" data-testid="where-things">
      <div className="ph-apps">
        <div className="ph-apps-head">
          <h2>Apps</h2>
          {runAll && <div className="ph-apps-run" data-testid="apps-run-all">{runAll}</div>}
          {canAdd && <AddService onAdd={onAddService} />}
        </div>
        {addState && (
          <div className={`ph-apps-note ${addState.phase === 'failed' ? 'ph-error' : 'ph-muted ph-small'}`} role={addState.phase === 'failed' ? 'alert' : 'status'}>{addState.text}</div>
        )}
        {sandbox ? (
          <AppsTable
            canDelete={canEdit}
            onDeleted={onServiceDeleted}
            canRun={canRun}
            onChanged={onChanged}
            onOpenApp={onOpenApp}
            onOpenCode={onOpenCode}
            onOpenFiles={onOpenFiles}
            onOpenLogs={onOpenLogs}
            onOpenService={onOpenService}
            projectId={projectId}
            runner={runner}
            services={services}
            tenantId={tenantId}
          />
        ) : (
          <ul>
            {services.map(([key, service]) => (
              <AppRow
                key={key}
                onOpen={onOpenService}
                onOpenFiles={onOpenFiles}
                projectId={projectId}
                service={service}
                serviceKey={key}
                technologies={technologies}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

WhereThings.propTypes = {
  // After a service was deleted (the Home re-reads the project's services).
  onServiceDeleted: PropTypes.func,
  // Start all / Stop all, in the card's header (DashboardView's run actions).
  runAll: PropTypes.node,
  // The .zip or folder being added: { phase, text } (StartBy's useImportIntoProject).
  addState: PropTypes.object,
  canEdit: PropTypes.bool,
  canRun: PropTypes.bool,
  // Owners and editors: ('import', collected) for a .zip or a folder, ('git') for a repository.
  onAddService: PropTypes.func,
  // No apps yet: opens the chat to ask for one (null where chat is off).
  onAskAssistant: PropTypes.func,
  onChanged: PropTypes.func,
  onOpenFiles: PropTypes.func.isRequired,
  onOpenLogs: PropTypes.func,
  onOpenApp: PropTypes.func,
  onOpenCode: PropTypes.func,
  onOpenService: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  runner: PropTypes.func.isRequired,
  sandbox: PropTypes.bool,
  services: PropTypes.array.isRequired,
  technologies: PropTypes.arrayOf(PropTypes.string),
  tenantId: PropTypes.string,
};
